import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Check, CheckCheck, Loader2, MessageSquarePlus, Paperclip, Send, X } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { UploadProgressBar } from "@/components/ui/upload-progress-bar";
import { StartConversationDialog } from "@/components/StartConversationDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import {
  useConversationMessages,
  useConversations,
  useMarkConversationRead,
  useSendDirectMessage,
} from "@/hooks/useDirectMessages";
import { usePresence } from "@/hooks/usePresence";
import { formatDateTime, initials } from "@/lib/format";
import type { ConversationRow, DirectMessageRow } from "@/lib/types";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// /messages — Telegram-style split-panel chat.
//   left  : conversation list (avatars, last-message preview, unread badge)
//   right : active conversation (messages + composer)
// On mobile the layout collapses to one column — picking a conversation
// hides the list, the X button on the header brings it back.
// ---------------------------------------------------------------------------
export default function Messages() {
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const activeId = params.id;

  const [newOpen, setNewOpen] = useState(false);
  const { data: convs, isLoading: convsLoading } = useConversations();
  const mark = useMarkConversationRead();

  // Mark the conversation read whenever the user lands on it (matches
  // what users expect — opening the chat == "I've seen it").
  useEffect(() => {
    if (activeId) mark.mutate(activeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  return (
    <div className="-m-4 flex h-[calc(100vh-3.5rem)] sm:-m-6 sm:h-[calc(100vh-3.5rem)] lg:-m-8">
      {/* --- LEFT: conversation list --- */}
      <aside
        className={cn(
          "flex w-full shrink-0 flex-col border-r border-border/60 bg-card/30 sm:w-72 lg:w-80",
          // On mobile, hide the list when a conversation is open.
          activeId && "hidden sm:flex",
        )}
      >
        <header className="flex items-center justify-between gap-2 border-b border-border/60 px-4 py-3">
          <h2 className="text-sm font-bold uppercase tracking-wide">Messages</h2>
          <Button size="sm" onClick={() => setNewOpen(true)} className="h-7 px-2 text-xs">
            <MessageSquarePlus className="mr-1 h-3.5 w-3.5" />New
          </Button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {convsLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : !convs || convs.results.length === 0 ? (
            <EmptyConversationsState onNew={() => setNewOpen(true)} />
          ) : (
            <ul>
              {convs.results.map((c) => (
                <ConversationListItem
                  key={c.id}
                  conv={c}
                  active={c.id === activeId}
                  onPick={() => navigate(`/messages/${c.id}`)}
                />
              ))}
            </ul>
          )}
        </div>
      </aside>

      {/* --- RIGHT: active conversation pane --- */}
      <main className="flex min-w-0 flex-1 flex-col">
        {activeId ? (
          <ConversationView
            conversationId={activeId}
            onClose={() => navigate("/messages")}
            conversation={convs?.results.find((c) => c.id === activeId)}
          />
        ) : (
          <EmptyChatState />
        )}
      </main>

      <StartConversationDialog open={newOpen} onOpenChange={setNewOpen} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// List item
// ---------------------------------------------------------------------------
function ConversationListItem({
  conv, active, onPick,
}: {
  conv: ConversationRow;
  active: boolean;
  onPick: () => void;
}) {
  const { isOnline } = usePresence();
  const other = conv.other_user;
  const name = other?.full_name || other?.email || "Unknown user";
  const last = conv.last_message;
  const online = isOnline(other?.id ?? null);

  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        className={cn(
          "flex w-full items-center gap-3 border-b border-border/40 px-3 py-3 text-left transition hover:bg-accent/40",
          active && "bg-accent/60",
        )}
      >
        <div className="relative shrink-0">
          <Avatar className="h-10 w-10 ring-1 ring-border">
            {other?.avatar_url && <AvatarImage src={other.avatar_url} alt="" />}
            <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-xs font-semibold text-primary-foreground">
              {initials(name)}
            </AvatarFallback>
          </Avatar>
          <span
            className={cn(
              "absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full ring-2 ring-card",
              online ? "bg-emerald-500" : "bg-slate-400/70",
            )}
            aria-label={online ? "Online" : "Offline"}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className={cn(
              "truncate text-sm",
              conv.unread_count > 0 ? "font-bold" : "font-semibold",
            )}>
              {name}
            </span>
            {last && (
              <span className="shrink-0 text-[10px] text-muted-foreground">
                {formatDateTime(last.created_at)}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <span className={cn(
              "truncate text-xs",
              conv.unread_count > 0 ? "text-foreground" : "text-muted-foreground",
            )}>
              {last ? (last.content || "(attachment)") : <em className="text-muted-foreground">No messages yet</em>}
            </span>
            {conv.unread_count > 0 && (
              <span className="ml-auto inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-primary-foreground">
                {conv.unread_count > 99 ? "99+" : conv.unread_count}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Right pane — messages + composer
// ---------------------------------------------------------------------------
function ConversationView({
  conversationId, onClose, conversation,
}: {
  conversationId: string;
  onClose: () => void;
  conversation: ConversationRow | undefined;
}) {
  const { data: user } = useCurrentUser();
  const { isOnline } = usePresence();
  const { data: messages, isLoading } = useConversationMessages(conversationId);
  const send = useSendDirectMessage();

  const [content, setContent] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didInitialScroll = useRef(false);

  const other = conversation?.other_user;
  const otherName = other?.full_name || other?.email || "Conversation";

  // Auto-scroll: jump to bottom on open, then again on every new
  // message (own or incoming) when the user is near the bottom.
  const msgCount = messages?.results.length ?? 0;
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || msgCount === 0) return;
    requestAnimationFrame(() => {
      if (!didInitialScroll.current) {
        el.scrollTop = el.scrollHeight;
        didInitialScroll.current = true;
        return;
      }
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (dist < 200) el.scrollTop = el.scrollHeight;
    });
  }, [msgCount]);

  // Reset initial-scroll guard when switching conversations.
  useEffect(() => { didInitialScroll.current = false; }, [conversationId]);

  const canSubmit = content.trim().length > 0 || files.length > 0;
  const isSending = send.isPending || uploadProgress !== null;

  function submitNow() {
    if (!canSubmit) return;
    if (files.length > 0) setUploadProgress(-1);
    send.mutate(
      {
        conversationId,
        content: content.trim(),
        files: files.length ? files : undefined,
        onUploadProgress: setUploadProgress,
      },
      {
        onSuccess: () => {
          setContent("");
          setFiles([]);
          setUploadProgress(null);
          requestAnimationFrame(() => {
            const el = scrollRef.current;
            if (el) el.scrollTop = el.scrollHeight;
          });
        },
        onError: () => setUploadProgress(null),
      },
    );
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== "Enter") return;
    if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.nativeEvent.isComposing) return;
    e.preventDefault();
    submitNow();
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header — Telegram-style row with the other user's avatar + name */}
      <header className="flex items-center gap-3 border-b border-border/60 bg-card px-4 py-2.5 shadow-soft">
        <Button
          variant="ghost" size="icon" className="h-8 w-8 sm:hidden"
          onClick={onClose} title="Back to list" aria-label="Back to list"
        >
          <X className="h-4 w-4" />
        </Button>
        <div className="relative shrink-0">
          <Avatar className="h-9 w-9 ring-1 ring-border">
            {other?.avatar_url && <AvatarImage src={other.avatar_url} alt="" />}
            <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-xs font-semibold text-primary-foreground">
              {initials(otherName)}
            </AvatarFallback>
          </Avatar>
          <span
            className={cn(
              "absolute bottom-0 right-0 h-2 w-2 rounded-full ring-2 ring-card",
              other && isOnline(other.id) ? "bg-emerald-500" : "bg-slate-400/70",
            )}
          />
        </div>
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{otherName}</div>
          <div className="text-[11px] text-muted-foreground">
            {other && isOnline(other.id) ? "Online" : "Offline"}
          </div>
        </div>
      </header>

      {/* Message stream */}
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-2/3" />
            ))}
          </div>
        ) : !messages || messages.results.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            No messages yet — say hello.
          </div>
        ) : (
          messages.results.map((m, i, arr) => {
            // Telegram-style grouping: show the avatar / sender-name
            // only on the FIRST message in a run from the same sender,
            // and round the bottom corner of every non-last bubble in
            // the run for the "stacked" look.
            const prev = i > 0 ? arr[i - 1] : null;
            const next = i + 1 < arr.length ? arr[i + 1] : null;
            const firstOfRun = !prev || prev.sender_id !== m.sender_id;
            const lastOfRun  = !next || next.sender_id !== m.sender_id;
            return (
              <MessageBubble
                key={m.id}
                msg={m}
                isMine={!!user && m.sender_id === user.id}
                firstOfRun={firstOfRun}
                lastOfRun={lastOfRun}
              />
            );
          })
        )}
      </div>

      {/* Composer */}
      <div className="space-y-2 border-t border-border/60 bg-card px-4 py-3">
        {uploadProgress !== null && files.length > 0 && (
          <UploadProgressBar
            progress={uploadProgress}
            totalBytes={files.reduce((s, f) => s + f.size, 0)}
          />
        )}
        {files.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${i}`}
                className="inline-flex items-center gap-1.5 rounded-full border bg-muted/40 py-1 pl-2.5 pr-1 text-xs"
              >
                <Paperclip className="h-3 w-3 text-muted-foreground" />
                <span className="max-w-[10rem] truncate">{f.name}</span>
                <button
                  type="button"
                  onClick={() => setFiles((p) => p.filter((_, idx) => idx !== i))}
                  className="ml-1 rounded-full p-0.5 hover:bg-background"
                  aria-label={`Remove ${f.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-end gap-2">
          <Button
            type="button" variant="ghost" size="icon" className="h-9 w-9 shrink-0"
            disabled={isSending}
            onClick={() => fileInputRef.current?.click()}
            title="Attach files"
          >
            <Paperclip className="h-4 w-4" />
          </Button>
          <input
            ref={fileInputRef} type="file" multiple className="hidden"
            onChange={(e) => {
              const picked = Array.from(e.target.files ?? []);
              if (picked.length) setFiles((p) => [...p, ...picked]);
              e.target.value = "";
            }}
          />
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Write a message…"
            rows={1}
            className="min-h-[40px] max-h-32 flex-1 resize-none py-2"
          />
          <Button
            type="button" size="sm" className="h-9 shrink-0"
            disabled={!canSubmit}
            onClick={submitNow}
          >
            {isSending
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bubble — "mine on the right" / "theirs on the left" Telegram pattern
// ---------------------------------------------------------------------------
function MessageBubble({
  msg, isMine, firstOfRun, lastOfRun,
}: {
  msg: DirectMessageRow;
  isMine: boolean;
  /** First message in a run from this sender — render avatar + name. */
  firstOfRun: boolean;
  /** Last message in a run from this sender — render timestamp + ticks
   *  here (the in-run bubbles above stay clean, like Telegram). */
  lastOfRun: boolean;
}) {
  if (msg.is_deleted) {
    return (
      <div className="text-center text-[11px] italic text-muted-foreground">
        Message deleted
      </div>
    );
  }

  const senderAvatar = msg.sender?.avatar_url;

  return (
    <div className={cn(
      "flex w-full items-end gap-2",
      isMine ? "justify-end" : "justify-start",
      // Tighter vertical gap WITHIN a run; the parent's space-y-2
      // applies BETWEEN runs.
      !firstOfRun && "-mt-1",
    )}>
      {/* INCOMING — avatar on the left, on the LAST bubble of the run
          (Telegram-style — sits at the chin of the last message). */}
      {!isMine && (
        <div className="w-7 shrink-0">
          {lastOfRun ? (
            <Avatar className="h-7 w-7 ring-1 ring-border">
              {senderAvatar && <AvatarImage src={senderAvatar} alt="" />}
              <AvatarFallback className="bg-gradient-to-br from-primary to-emerald-700 text-[10px] font-semibold text-primary-foreground">
                {initials(msg.sender_name || "")}
              </AvatarFallback>
            </Avatar>
          ) : null}
        </div>
      )}

      <div className={cn("max-w-[75%]")}>
        {firstOfRun && !isMine && (
          <div className="mb-0.5 px-2 text-[10px] font-semibold text-foreground/80">
            {msg.sender_name}
          </div>
        )}
        <div
          className={cn(
            "px-3 py-2 text-sm shadow-soft",
            // Bubble corners: full rounding except where the bubble
            // connects to another one in the same run.
            isMine
              ? cn(
                  "bg-primary text-primary-foreground",
                  firstOfRun ? "rounded-2xl rounded-tr-md" : "rounded-l-2xl rounded-tr-md",
                  lastOfRun  ? "" : "rounded-br-md",
                )
              : cn(
                  "bg-card border border-border/60",
                  firstOfRun ? "rounded-2xl rounded-tl-md" : "rounded-r-2xl rounded-tl-md",
                  lastOfRun  ? "" : "rounded-bl-md",
                ),
          )}
        >
          {msg.content && (
            <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
          )}
          {msg.attachments.length > 0 && (
            <ul className={cn("space-y-1", msg.content && "mt-2")}>
              {msg.attachments.map((a) =>
                a.is_image ? (
                  <li key={a.id}>
                    <a href={a.file_url ?? "#"} target="_blank" rel="noreferrer" className="block overflow-hidden rounded-md">
                      <img src={a.file_url ?? ""} alt={a.original_name} loading="lazy"
                           className="max-h-64 w-full object-cover" />
                    </a>
                  </li>
                ) : (
                  <li key={a.id}>
                    <a
                      href={a.file_url ?? "#"} target="_blank" rel="noreferrer"
                      className={cn(
                        "inline-flex items-center gap-2 rounded-md border px-2 py-1 text-xs",
                        isMine
                          ? "border-primary-foreground/30 bg-primary-foreground/10 text-primary-foreground"
                          : "border-border/60 bg-muted hover:bg-accent/40",
                      )}
                    >
                      <Paperclip className="h-3 w-3" />
                      <span className="truncate">{a.original_name}</span>
                      <span className="opacity-70">{a.human_size}</span>
                    </a>
                  </li>
                )
              )}
            </ul>
          )}
        </div>
        {/* Meta line — timestamp + read tick. Only on the LAST bubble
            of a run so a chain of quick messages doesn't get cluttered. */}
        {lastOfRun && (
          <div className={cn(
            "mt-0.5 flex items-center gap-1 px-2 text-[10px] text-muted-foreground",
            isMine && "justify-end",
          )}>
            <span>{formatDateTime(msg.created_at)}</span>
            {isMine && (
              msg.read_by_other
                ? <CheckCheck className="h-3 w-3 text-sky-500" aria-label="Read" />
                : <Check      className="h-3 w-3"              aria-label="Sent" />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function EmptyConversationsState({ onNew }: { onNew: () => void }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      <div className="text-sm text-muted-foreground">No conversations yet.</div>
      <Button size="sm" onClick={onNew}>
        <MessageSquarePlus className="mr-1.5 h-3.5 w-3.5" />Start a chat
      </Button>
    </div>
  );
}

function EmptyChatState() {
  return (
    <Card className="m-6 border-dashed">
      <CardContent className="flex flex-col items-center justify-center gap-2 py-16 text-center text-sm text-muted-foreground">
        <MessageSquarePlus className="h-8 w-8" />
        Pick a conversation or start a new one.
      </CardContent>
    </Card>
  );
}
